import type { MissingMeetingLink } from '../../types';

/**
 * MED-1365 QA round 6 (comment 12672): the words for a meeting link that a
 * coach still needs. A cohort on Zoom, Teams or Google Meet takes the coach's
 * link for that platform only, so with no link its sessions get none from
 * the coach. Shown on screen only; no email is sent.
 */

export const PLATFORM_LABEL: Record<MissingMeetingLink['platform'], string> = {
  zoom: 'Zoom',
  teams: 'Microsoft Teams',
  google_meet: 'Google Meet',
};

/** The field of the link in the coach's Edit Profile window. */
export const PLATFORM_FIELD: Record<
  MissingMeetingLink['platform'],
  'zoom_link' | 'teams_link' | 'google_meet_link'
> = {
  zoom: 'zoom_link',
  teams: 'teams_link',
  google_meet: 'google_meet_link',
};

/**
 * QA round 7: show the link field that a prompt opened. The field is low in
 * the Edit Profile window, so it goes to the middle of the view, then takes
 * the focus without a second scroll. Call it after the open animation.
 */
export function revealField(
  field: Pick<HTMLElement, 'scrollIntoView' | 'focus'> | null | undefined
): void {
  if (!field) return;
  field.scrollIntoView({ block: 'center' });
  field.focus({ preventScroll: true });
}

/** "A", "A and B" or "A, B and C". */
function nameList(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** The title, the body and the button of one missing link. */
export function missingLinkText(
  link: MissingMeetingLink,
  audience: 'coach' | 'admin'
): { title: string; body: string; action: string } {
  const label = PLATFORM_LABEL[link.platform];
  const names = nameList(link.cohorts.map(c => c.name));
  const uses = link.cohorts.length === 1 ? 'uses' : 'use';
  return audience === 'coach'
    ? {
        title: `Add your ${label} link`,
        body: `${names} ${uses} ${label} for sessions. Your profile has no ${label} link, so these sessions do not get your link.`,
        action: `Add ${label} link`,
      }
    : {
        title: `No ${label} link`,
        body: `${names} ${uses} ${label} for sessions. The coach's profile has no ${label} link, so these sessions do not get the coach's link.`,
        action: '',
      };
}
